import React, { useCallback, useEffect, useState } from "react";

import { Avatar, Badge, Menu, Dropdown, Popover } from "antd";

import {
  AppstoreOutlined,
  SettingOutlined,
  LogoutOutlined,
  BellOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import photo from "@/style/images/photo.png";

import history from "@/utils/history";
import uniqueId from "@/utils/uinqueId";
import NotificationPopover from "@/components/NotificationPopover";
import HrmAttendanceWidget from "@/app/HeaderContent/HrmAttendanceWidget";
import { getNotificationInboxUnreadCount } from "@/services/notificationInboxService";

// Import our language utilities
import {
  getCurrentLanguage,
  setLanguage,
  AVAILABLE_LANGUAGES,
  t,
} from "@/utils/language";

export default function HeaderContent() {
  const UNREAD_POLL_INTERVAL_MS = 60000;
  const { SubMenu } = Menu;
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [userData, setUserData] = useState({
    username: "",
    mobileNumber: "",
  });
  const [isNotificationPopoverOpen, setNotificationPopoverOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const response = await getNotificationInboxUnreadCount();
      const count = Number(response?.count || 0);
      setUnreadCount(Number.isFinite(count) ? count : 0);
    } catch (error) {
      console.error("Failed to load unread notification count", error);
    }
  }, []);

  const handleNotificationVisibleChange = (visible) => {
    setNotificationPopoverOpen(visible);
    if (visible) {
      refreshUnreadCount();
    }
  };

  const handleViewAllNotifications = () => {
    setNotificationPopoverOpen(false);
    history.push("/notifications");
  };

  useEffect(() => {
    // Get user data from localStorage
    try {
      const authData = localStorage.getItem("auth");
      if (authData) {
        const parsedData = JSON.parse(authData);
        setUserData({
          username: parsedData?.strName || "",
          mobileNumber: parsedData.strMobileNo || "",
        });
      }
    } catch (error) {
      console.error("Error parsing auth data from localStorage:", error);
    }

    // Initialize language
    setCurrentLang(getCurrentLanguage());

    // Listen for language changes from other components
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener("languagechange", handleLanguageChange);

    return () => {
      window.removeEventListener("languagechange", handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    refreshUnreadCount();

    const intervalId = window.setInterval(() => {
      if (document.hidden) return;
      refreshUnreadCount();
    }, UNREAD_POLL_INTERVAL_MS);

    const unlisten = history.listen(() => {
      refreshUnreadCount();
    });

    const handleNotificationsChanged = () => {
      refreshUnreadCount();
    };
    window.addEventListener("notifications:changed", handleNotificationsChanged);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("notifications:changed", handleNotificationsChanged);
      if (typeof unlisten === "function") {
        unlisten();
      }
    };
  }, [refreshUnreadCount]);

  // Handle language change
  const handleLanguageChange = (languageCode) => {
    setLanguage(languageCode);
    setCurrentLang(languageCode);
  };

  // Language selection dropdown
  const languageMenu = (
    <Menu>
      {Object.entries(AVAILABLE_LANGUAGES).map(([code, langData]) => (
        <Menu.Item
          key={code}
          onClick={() => handleLanguageChange(code)}
          style={{
            fontWeight: currentLang === code ? "bold" : "normal",
            backgroundColor: currentLang === code ? "#f0f0f0" : "transparent",
          }}
        >
          <span style={{ marginRight: "8px" }}>{langData.flag}</span>
          {langData.name}
        </Menu.Item>
      ))}
    </Menu>
  );

  const profileDropdown = (
    <div
      className="profileDropdown whiteBox shadow"
      style={{ minWidth: "200px" }}
    >
      <div className="pad15">
        <Avatar
          size="large"
          className="last"
          src={photo}
          style={{ float: "left" }}
        />
        <div className="info">
          <p className="strong">{userData.username}</p>
          <p>{userData.mobileNumber}</p>
        </div>
      </div>
      <div className="line"></div>
      <div>
        <Menu>
          {/* Language Settings */}
          <SubMenu
            key={`${uniqueId()}`}
            icon={<GlobalOutlined />}
            title={t("header.language")}
          >
            {Object.entries(AVAILABLE_LANGUAGES).map(([code, langData]) => (
              <Menu.Item
                key={code}
                onClick={() => handleLanguageChange(code)}
                style={{
                  fontWeight: currentLang === code ? "bold" : "normal",
                  backgroundColor:
                    currentLang === code ? "#f0f0f0" : "transparent",
                }}
              >
                <span style={{ marginRight: "8px" }}>{langData.flag}</span>
                {t(`header.${code === "en" ? "english" : "arabic"}`)}
              </Menu.Item>
            ))}
          </SubMenu>

          {/* Settings Menu Item */}
          <Menu.Item
            icon={<SettingOutlined />}
            key={`${uniqueId()}`}
            onClick={() => history.push("/settings/notifications")}
          >
            {t("header.settings")}
          </Menu.Item>

          {/* Logout Menu Item */}
          <Menu.Item
            icon={<LogoutOutlined />}
            key={`${uniqueId()}`}
            onClick={async () => {
              await history.push("/logout");
            }}
          >
            {t("header.logout")}
          </Menu.Item>
        </Menu>
      </div>
    </div>
  );

  return (
    <div
      className="headerIcon"
      style={{
        position: "absolute",
        right: currentLang === "ar" ? "auto" : 0,
        left: currentLang === "ar" ? 0 : "auto",
        zIndex: "99",
        direction: AVAILABLE_LANGUAGES[currentLang].dir,
      }}
    >
      <HrmAttendanceWidget />

      {/* Global language selector button */}
      <Dropdown
        overlay={languageMenu}
        trigger={["click"]}
        placement="bottomRight"
      >
        <Avatar
          icon={<GlobalOutlined />}
          style={{ backgroundColor: "#1890ff" }}
        />
      </Dropdown>

      <Popover
        content={
          <NotificationPopover
            open={isNotificationPopoverOpen}
            onViewAll={handleViewAllNotifications}
            onRefreshUnreadCount={refreshUnreadCount}
          />
        }
        trigger="click"
        placement="bottomRight"
        open={isNotificationPopoverOpen}
        onOpenChange={handleNotificationVisibleChange}
      >
        <Badge
          count={unreadCount}
          overflowCount={99}
          size="small"
          style={{ float: "right", marginLeft: 10, marginTop: 15 }}
        >
          <Avatar
            icon={<BellOutlined />}
            title={t("header.notifications")}
            style={{ cursor: "pointer", float: "none", marginLeft: 0, marginTop: 0 }}
          />
        </Badge>
      </Popover>

      <Avatar icon={<AppstoreOutlined />} />

      <Dropdown
        overlay={profileDropdown}
        trigger={["click"]}
        placement={currentLang === "ar" ? "bottomLeft" : "bottomRight"}
      >
        <Avatar className="last" src={photo} />
      </Dropdown>
    </div>
  );
}
